import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  IStyles,
  ImageWrapper,
  PrimaryButton,
  SecondaryOutlinedButton,
  StudentSubjectPopup,
  deserify,
  firstLetterImage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import CloseIcon from '@mui/icons-material/Close';
import { useGlobalContext } from '../../app/Context/GlobalContextProvider';
import {
  setHomeSelectedBookId,
  setHomeSelectedSubjectId,
} from '../../pages/Home/reducer/homeDashboard.slice';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import {
  HOME,
  HOMEWORK_STUDENT_DASHBOARD,
  LEARN_DASHBOARD,
  REPORT,
} from '../../routeHandling/RoutesNomenclature';
import { getSubjectsMap } from '../../utils/icons';
import { useSideBarOptions } from '../../utils/sidebarOptions';

import { interactionEvent } from '../../pages/Auth/Login/login_logout.events';
import {
  setActiveHomeworSessionId,
  setStudentHwUploadData,
} from '../../pages/Homework/reducer/homework.slice';
import { useFilteredLearnSubjects } from '../../utils/ssoUserHooks';
import {
  aiBookCloseEvent,
  aiBookOpenEvent,
} from '../../pages/Learn/utils_learn/learn.events';
import { useDownloadContext } from '../../app/Context/DownloadContextProviderV2';
import {
  useLanguageSelectorEnabled,
  usePalMode,
  useWhiteLabeledUrl,
} from '../../utils/customHooks';
import LanguageSelector from '../LanguageSelector';
import { useTranslation } from 'react-i18next';

const styles: IStyles = {
  root: {
    width: '100vw',
    height: { xs: pxToRem(60), md: pxTovW(100) },
    background: 'grey',
    position: 'fixed',
    borderBottomWidth: pxTovW(1),
    borderBottomStyle: 'solid',
    borderBottomColor: 'neutral.grey',
    boxSizing: 'border-box',
    // border: '1px solid red',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: { xs: `0 ${pxToRem(20)}`, lg: `0 ${pxTovW(240)}` },
    bgcolor: 'common.white',
    zIndex: 1,
  },

  leftArrow: {
    cursor: 'pointer',
    width: { xs: pxToRem(14), md: pxTovW(19) },
    height: { xs: pxToRem(13), md: pxTovW(26) },
    flexBasis: { xs: pxToRem(14), md: pxTovW(19) },
  },
  logo: {
    cursor: 'pointer',
    flexFlow: '1',
    // width: { xs: pxToRem(75), md: pxTovW(100) },
    // height: { xs: pxToRem(42), md: pxTovW(56) },
    width: 'auto',
    height: '100%',
    maxHeight: { xs: pxToRem(52), md: pxTovW(76) },
  },

  userBox: {
    display: { xs: 'none', md: 'flex' },
    alignItems: 'center',
    gap: pxTovW(10),
    cursor: 'pointer',
  },

  menu: {
    mt: pxTovW(20),
    '& .MuiMenu-list': {
      width: { md: pxTovW(450), lg: pxTovW(352) },
      p: { md: `${pxTovW(12)}`, lg: `${pxTovW(12)} ${pxTovW(10)}` },
      borderRadius: pxTovW(10),
      boxShadow: `0 0 ${pxTovW(10)} #00000038`,
    },
    '& .MuiMenu-paper': {
      borderRadius: pxTovW(10),
    },
  },
  menuItem: {
    borderBottom: '1px solid #D2D2D2',
    p: { md: `${pxTovW(20)} ${pxTovW(15)}` },
    '&:last-child': {
      borderBottom: '1px solid transparent',
    },
    '&:hover': {
      backgroundColor: '#F2F2F2',
      borderBottom: '1px solid transparent',
      borderRadius: pxTovW(10),
    },
  },

  userImage: {
    width: pxTovW(55),
    height: pxTovW(55),
    borderRadius: '50%',
  },

  downArrow: {
    cursor: 'pointer',
    width: pxTovW(13),
    height: pxTovW(7),
  },

  menuTriple: {
    width: pxToRem(16),
    height: pxToRem(14),
  },
  performanceBtnStyles: {
    m: 0,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(7),
    px: pxToRem(14),
    py: pxToRem(7),
    borderRadius: pxToRem(10),
    border: '1.5px solid #e2e8f0',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    color: '#475569',
    fontSize: pxToRem(13),
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: '#667eea',
      color: '#667eea',
      background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
      '& .perf-text': {
        color: '#667eea',
      },
    },
  },
};

interface IProps {
  sidebarDisplay: boolean;
  setSidebarDisplay: (arg0: boolean) => void;
}

export default function Header(props: IProps) {
  const { t } = useTranslation();
  const { setSidebarDisplay, sidebarDisplay } = props;
  const currentPage = window.location.pathname;
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const {
    userInfo,
    session_mode,
    is_sso_user,
    sso_subject_ids,
    is_logged_in_offline,
  } = deserify(useAppSelector((state) => state.auth));
  const isOfflineFrontend = isOffline || is_logged_in_offline;
  const { selected_book_id, selected_subject_id } = deserify(
    useAppSelector((state) => state.home)
  );
  const options = useSideBarOptions();
  const isRootOrHome =
    location.pathname === '/' || location.pathname === '/home';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const isIpadOnly = !isMobile && isIpad;

  const lessonSessionId =
    new URLSearchParams(location.search).get('lessonSessionId') || undefined;

  const learnSubjects = userInfo?.learnSubjects || [];
  const subMap = getSubjectsMap(learnSubjects);
  //^ StudentSubjectPopup
  const [modalState, setModalState] = useState(false);
  const navigate = useNavigate();
  const {
    selectedFunction,
    setSelectedFunction,
    selectedLogoClickHandler,
    myPerformanceClickHandler,
  } = useGlobalContext();

  const {
    selected_module_lessons,
    selected_lesson_content,
    selected_resource_content,
    selected_resource_info,
    selected_resource_session_id,
  } = deserify(useAppSelector((state) => state.learn));
  const { performance_id } = deserify(
    useAppSelector((state) => state.performance)
  );
  const { selected_homework_data, active_homework_session_id } = deserify(
    useAppSelector((state) => state.homework)
  );
  const school_logo_url = useWhiteLabeledUrl();
  const isLanguageSelectorEnabled = useLanguageSelectorEnabled();
  const { isPalMode } = usePalMode();

  const goBack = async () => {
    if (selectedFunction) {
      selectedFunction();
      setSelectedFunction(null);
      return;
    } else if (options.some((elem) => elem.route === currentPage)) {
      navigate(HOME);
    } else {
      navigate(-1);
    }
    await interactionEvent({
      url: '',
      context: 'left_arrow',
      name: 'BACK',
      pathSegments: pathSegments,
      lessonSesssionId: lessonSessionId,
      resourceSesssionId: selected_resource_session_id?.toString(),
      homeworkSesssionId: active_homework_session_id?.toString(),
      isOffline: isOffline,
    });
  };

  const commonAiCloseEvents = async () => {
    // if no selected_subject_id then that means no book should be open
    // if (currentPage !== HOME && !selected_subject_id) {
    //   aiBookCloseEvent(selected_book_id);
    // }

    // aiChapterCloseEvent(selectedChapterResources?.chapterId);
    // aiTopicCloseEvent(selected_module_lessons?.moduleId);
    // aiLessonCloseEvent(selected_lesson_content?.lessonId);
    // aiResourceCloseEvent(selected_resource_content?.parsedData.resourceId);
    // aiResourcePageCloseEvent(
    //   selected_resource_content?.parsedData.resourceId,
    //   selected_resource_info?.pageNumber
    // );
    // aiContentCloseEvent({
    //   pageNumber: selected_resource_info?.pageNumber,
    //   pageContent: selected_resource_info?.pageContent,
    // });
    // aiPerformanceCloseEvent(performance_id);
    // dispatch(setPerformanceId(undefined));
    // aiHomeworkCloseEvent({ homeworkId: selected_homework_data?.homeworkId });
    // aiHomeworkReviewCloseEvent({
    //   homeworkId: selected_homework_data?.homeworkId,
    // });

    await interactionEvent({
      url: '',
      context: 'navigation_bar',
      name: 'GENEO',
      pathSegments: pathSegments,
      lessonSesssionId: lessonSessionId,
      resourceSesssionId: selected_resource_session_id?.toString(),
      homeworkSesssionId: active_homework_session_id?.toString(),
      isOffline: isOffline,
    });
    dispatch(setActiveHomeworSessionId(undefined));
    dispatch(setStudentHwUploadData([])); //not needed
  };

  const logoClickHandler = async () => {
    if (is_sso_user && sso_subject_ids && sso_subject_ids.length == 1) {
      if (session_mode == 'learn') {
        if (selectedLogoClickHandler) {
          await selectedLogoClickHandler();
        }
        navigate(`${LEARN_DASHBOARD}/${sso_subject_ids[0]}`);
        return;
      } else if (session_mode == 'assess') {
        if (selectedLogoClickHandler) {
          await selectedLogoClickHandler();
        }
        navigate(HOMEWORK_STUDENT_DASHBOARD);
        return;
      }
    }
    await commonAiCloseEvents();
    if (selectedLogoClickHandler) {
      selectedLogoClickHandler();
    } else {
      navigate(HOME);
    }
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = async (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLImageElement, MouseEvent>
      | React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget);
    await interactionEvent({
      url: '',
      context: 'Navigation_bar',
      name: 'MENU',
      pathSegments: pathSegments,
      lessonSesssionId: lessonSessionId,
      resourceSesssionId: selected_resource_session_id?.toString(),
      homeworkSesssionId: active_homework_session_id?.toString(),
      isOffline: isOffline,
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const filteredLearnSubjects = useFilteredLearnSubjects();

  // ----------------------------------------------------

  const showForSsoUser = () => {
    const hasRouteSegment = (route: string) =>
      pathSegments.includes(route.replace(/^\//, ''));

    if (
      is_sso_user &&
      session_mode === 'learn' &&
      sso_subject_ids &&
      sso_subject_ids.length == 1 &&
      hasRouteSegment(LEARN_DASHBOARD)
    ) {
      return false;
    } else if (
      is_sso_user &&
      session_mode === 'assess' &&
      sso_subject_ids &&
      sso_subject_ids.length == 1 &&
      hasRouteSegment(HOMEWORK_STUDENT_DASHBOARD)
    ) {
      return false;
    }
    return true;
  };

  // ----------------------------------------------------

  return (
    <Box sx={styles.root}>
      {!showForSsoUser() ? (
        <Box></Box>
      ) : currentPage !== HOME ? (
        <Box onClick={goBack}>
          <KeyboardBackspaceIcon fontSize="medium" sx={{ cursor: 'pointer' }} />
        </Box>
      ) : (
        <Box sx={{ display: 'none' }}></Box>
      )}

      <Box
        // onClick={logoClickHandler}
        sx={{
          display: 'flex',
          gap: { xs: pxToRem(14), md: pxTovW(25) },
          margin: { xs: 'auto', md: 'unset' },
          alignItems: 'center',
          height: '100%',
        }}
      >
        {school_logo_url ? (
          <Box
            component="img"
            src={school_logo_url}
            sx={styles.logo}
            alt={'logo'}
            onClick={logoClickHandler}
          />
        ) : (
          <ImageWrapper
            name="geneo-logo"
            type="png"
            parentFolder="images"
            styles={{
              ...styles.logo,
              height: { xs: pxToRem(42), md: pxTovW(56) },
            }}
            onClick={logoClickHandler}
          />
        )}
        {currentPage === HOME && !isMobile ? (
          <Box>
            <SecondaryOutlinedButton
              size={isIpadOnly ? 'medium' : 'small'}
              witharrow={
                typeof selected_subject_id == 'undefined' ? true : false
              }
              onClick={async () => {
                await interactionEvent({
                  url: '',
                  context: 'Subject_selection',
                  name: 'CHOOSE_SUBJECT',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });

                setModalState(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h4" fontFamily={'poppins'}>
                {typeof selected_subject_id == 'number' &&
                subMap[selected_subject_id]
                  ? subMap[selected_subject_id].subjectName
                  : t('NAVBAR_CHOOSE_SUBJECT')}
              </Typography>
              {typeof selected_subject_id == 'number' && (
                <CloseIcon
                  sx={{
                    color: 'common.black',
                    fontSize: {
                      xs: pxToRem(14),
                      md: pxToRem(14),
                      lg: pxTovW(18),
                    },
                  }}
                  onClick={async (ev) => {
                    ev.stopPropagation();

                    await aiBookCloseEvent({
                      bookId: selected_book_id,
                      isOffline: isOffline,
                    });
                    // console.log('aiBookCloseEvent  - big screen');

                    dispatch(setHomeSelectedSubjectId(undefined));
                    dispatch(setHomeSelectedBookId(undefined));
                  }}
                />
              )}
            </SecondaryOutlinedButton>
          </Box>
        ) : (
          <Box sx={{ display: 'none' }}></Box>
        )}
      </Box>
      <Box
        display={'flex'}
        alignItems={'center'}
        gap={pxToRem(10)}
        mr={{ xs: 4, md: 0 }}
      >
        {isLanguageSelectorEnabled && <LanguageSelector />}
        {!!myPerformanceClickHandler && (
          <Box
            onClick={() => myPerformanceClickHandler()}
            sx={styles.performanceBtnStyles}
          >
            <PerformanceIcon size={18} />
            <Typography
              variant="smallText"
              fontFamily={'poppins'}
              className="perf-text"
              sx={{
                display: { xs: 'none', md: 'block' }, // hide text on mobile if needed
              }}
            >
              {t('PAL_PD_TITLE')}
            </Typography>
          </Box>
        )}
        {!isOfflineFrontend && isRootOrHome && !isPalMode && (
          <Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => navigate(REPORT)}
            >
              <ImageWrapper
                name={'report-center'}
                parentFolder="icons"
                type="png"
                styles={{
                  height: 'auto',
                  maxWidth: pxToRem(16),
                  marginRight: pxTovW(15),
                }}
              />
              <Typography variant="cardText">
                {t('NAVBAR_REPORT_CENTER')}
              </Typography>
            </Button>
          </Box>
        )}
        <Box onClick={(e) => handleClick(e)} sx={styles.userBox}>
          <ImageWrapper
            name="userBlue"
            path={
              getMediaBasePath(userInfo?.profilePics[0]?.url) ||
              firstLetterImage(userInfo?.firstName)
            }
            type="png"
            parentFolder="icons"
            styles={styles.userImage}
          />

          <Typography
            sx={{ textTransform: 'capitalize' }}
            variant="bodyText"
            fontWeight="medium"
          >
            {[userInfo?.firstName, userInfo?.middleName, userInfo?.lastName]
              .filter(Boolean)
              .join(' ')}
          </Typography>

          <Box pt={pxTovW(10)}>
            <KeyboardArrowDownIcon
              fontSize="medium"
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={(e) => e.stopPropagation()}
            sx={styles.menu}
          >
            {options.map((opt) => (
              <MenuItem
                key={opt.name}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                  opt.onClick();
                }}
                disabled={opt.disabled}
                sx={styles.menuItem}
              >
                {/* <Box
                sx={{
                  height: 'auto',
                  width: pxTovW(28),
                  marginRight: pxTovW(15),
                }}
              >
                <ImageWrapper
                  name={opt.icon}
                  parentFolder="icons"
                  type="png"
                  styles={{
                    height: 'auto',
                    maxWidth: pxTovW(22),
                    marginRight: pxTovW(15),
                  }}
                />
              </Box> */}

                <ImageWrapper
                  name={opt.icon}
                  parentFolder="icons"
                  type="png"
                  styles={{
                    height: 'auto',
                    maxWidth: pxTovW(22),
                    marginRight: pxTovW(15),
                  }}
                />

                <Typography variant="h3" color="neutral.royalBlue">
                  {opt.name}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      <Box
        onClick={async (e) => {
          await interactionEvent({
            url: '',
            context: 'Navigation_bar',
            name: 'MENU',
            pathSegments: pathSegments,
            lessonSesssionId: lessonSessionId,
            resourceSesssionId: selected_resource_session_id?.toString(),
            homeworkSesssionId: active_homework_session_id?.toString(),
            isOffline: isOffline,
          });
          setSidebarDisplay(!sidebarDisplay);
        }}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <ImageWrapper
          name="menuTriple"
          type="png"
          parentFolder="icons"
          styles={styles.menuTriple}
        />
      </Box>

      <StudentSubjectPopup
        modalState={modalState}
        setModalState={setModalState}
        displayData={filteredLearnSubjects.map((sub) => ({
          subject: sub.subjectName,
          icon: getMediaBasePath(sub.iconUrl, 'schoolnetStaticAssetBucket'),
          color: sub.textColor,
          onClick: async () => {
            await interactionEvent({
              url: 'Student_Home',
              context: 'Subject_selection',
              name: sub.subjectName.toUpperCase(),
              pathSegments: pathSegments,
              isOffline: isOffline,
            });

            await aiBookCloseEvent({
              bookId: selected_book_id,
              isOffline: isOffline,
            });
            // console.log('aiBookCloseEvent  - big screen');
            dispatch(setHomeSelectedSubjectId(sub.subjectId));
            dispatch(setHomeSelectedBookId(sub.bookId));

            //
            await aiBookOpenEvent({
              bookId: sub.bookId,
              schoolCourseId: sub.schoolCourseId,
              isOffline: isOffline,
            });

            setModalState(false);
          },
        }))}
        title={t('NAVBAR_CHOOSE_SUBJECT')}
      />
    </Box>
  );
}

const PerformanceIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
  </svg>
);
